import * as React from 'react';
import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import { fm } from 'Components/FormatMessage';
import { partialDateToValue, sortPartialDate } from 'neuro-utils';
import type { ISpecificNoDateData } from 'Components/DashboardTile/TileContentMaker/components/SpecificListNoDate';
import { getPreviousDocWithTreatments } from '../utils';
import { DescriptionText } from 'Components/DashboardTile/TileContentMaker/components/common/commonComponents';
import { filterByDocumentType } from 'Utility/documentHandling';
import { formatPartialDate } from 'Utility/date';

const DoctorsOrdersDash = ({ documents }: IDoctorsOrdersDash): React.JSX.Element => {
  const latestDoctorsOrdersDoc = filterByDocumentType(documents, 'doctorsOrders')
    .sort((a, b) => sortPartialDate(b.date, a.date))
    .at(0);

  const a = filterByDocumentType(documents, 'doctorsOrdersAdditional').at(0);

  const doctorsOrders = filterByDocumentType(documents, 'doctorsOrders').sort((a, b) =>
    sortPartialDate(b.date, a.date),
  );

  const latestTreatmentPeriodDoc = filterByDocumentType(documents, 'ninmtTreatmentPeriod')
    .sort((a, b) => sortPartialDate(b.date, a.date))
    .at(0);

  const doctorsOrdersInCurrentTreatmentPeriod = doctorsOrders.filter(
    d => sortPartialDate(d.date, latestTreatmentPeriodDoc?.date) !== -1,
  );

  const docDateCheck = !!(
    latestDoctorsOrdersDoc &&
    latestDoctorsOrdersDoc.date &&
    latestTreatmentPeriodDoc &&
    latestTreatmentPeriodDoc.date
  );
  const additionalDocsCheck =
    a && a.additionalDocuments && Array.isArray(a.additionalDocuments) && a.additionalDocuments.length > 0;
  const additionalQuestionnairesCheck =
    a &&
    a.additionalQuestionnaires &&
    Array.isArray(a.additionalQuestionnaires) &&
    a.additionalQuestionnaires.length > 0;

  // Orders that has not been "completed", meaning that orders that have not been signed as rea
  // Used to show amount of orders that have not been signed
  const uncompletedOrders = doctorsOrdersInCurrentTreatmentPeriod.filter(d => !d.completed);

  // Get previous document that has treatement protocol defined, rtns
  const previousDocWithSOTrtms = getPreviousDocWithTreatments(doctorsOrders, 'rtms', 'subjectOfTreatment');

  // Get previous document that has treatement protocol defined, tdcs
  const previousDocWithSOTtdcs = getPreviousDocWithTreatments(doctorsOrders, 'tdcs', 'subjectOfTreatment');

  // Object for additional documents
  const additionalDocuments = {
    title: fm('doctorsOrders.usedAdditionalDocuments'),
    value: additionalDocsCheck ? (
      <React.Fragment>
        {a.additionalDocuments?.map((t, i, arr) => (
          <span key={t}>
            {fm(`doctorsOrders.additionalDocuments.documentsUsed.opts.${t}`)}
            {i < arr.length - 1 ? ', ' : ''}
          </span>
        ))}
      </React.Fragment>
    ) : (
      '-'
    ),
    key: 'additionalDocuments',
  };
  // Object for additional questionnaires
  const additionalQuestionnaires = {
    title: fm('doctorsOrders.additionalDocuments.questionnairesUsed.title'),
    value: additionalQuestionnairesCheck ? (
      <>
        {a.additionalQuestionnaires?.map((t, i, arr) => (
          <span key={t}>
            {fm(`doctorsOrders.additionalDocuments.questionnairesUsed.opts.${t}`)}
            {i < arr.length - 1 ? ', ' : ''}
          </span>
        ))}
      </>
    ) : (
      '-'
    ),
    key: 'additionalQuestionnaires',
  };

  let tileData: ISpecificNoDateData[] = [];

  // If doctor's orders document exists
  if (
    docDateCheck &&
    partialDateToValue(latestDoctorsOrdersDoc.date) >= partialDateToValue(latestTreatmentPeriodDoc.date)
  ) {
    tileData = [
      {
        title: fm('doctorsOrders.latestOrder'),
        value: latestDoctorsOrdersDoc.date ? formatPartialDate(latestDoctorsOrdersDoc.date) : '-',
        key: 'date',
      },
      ...(Array.isArray(latestDoctorsOrdersDoc.treatmentType) && latestDoctorsOrdersDoc.treatmentType.length > 0
        ? [
            {
              title: fm('doctorsOrders.treatmentProtocol'),
              value:
                sortPartialDate(previousDocWithSOTrtms?.date, previousDocWithSOTtdcs?.date) === 1
                  ? previousDocWithSOTrtms?.date
                    ? formatPartialDate(previousDocWithSOTrtms.date)
                    : '-'
                  : previousDocWithSOTtdcs?.date
                    ? formatPartialDate(previousDocWithSOTtdcs.date)
                    : '-',
              key: 'treatmentProtocol',
            },
            {
              title: fm('doctorsOrders.uncompletedOrders'),
              value: uncompletedOrders.length,
              key: 'uncompletedOrders',
            },
          ]
        : []),
      additionalDocuments,
      additionalQuestionnaires,
    ];
  } else if (additionalDocsCheck || additionalQuestionnairesCheck) {
    // If there is no doctor's order document but additional documents have been defined
    tileData = [additionalDocuments, additionalQuestionnaires];
  }

  return (
    <>
      <TileContentMaker type="specificListNoDate" compact data={tileData} />
      {docDateCheck &&
      partialDateToValue(latestDoctorsOrdersDoc.date) < partialDateToValue(latestTreatmentPeriodDoc.date) ? (
        <DescriptionText>{fm('doctorsOrders.noOrdersCurrentPeriod')}</DescriptionText>
      ) : undefined}
    </>
  );
};

interface IDoctorsOrdersDash {
  documents: IDocumentWithTypeV2<'doctorsOrders' | 'doctorsOrdersAdditional' | 'ninmtTreatmentPeriod'>[];
}

export default DoctorsOrdersDash;
